import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_MAX_LENGTH,
  REACTION_FACES,
  type FeedbackCategory,
} from '@ready/shared';
import { useApi } from '@/lib/api';
import { useCurrentOrg } from '@/lib/current-org';
import { Button, ErrorText, Field, Select, Textarea } from '@/components/ui';
import { Modal } from './modal';

/**
 * "Feedback": a reaction and/or a short note, to the Nirvik team (the
 * default) or one of the user's organizations. The page it came from goes
 * along, and the user sees which.
 */
export function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const { orgs } = useCurrentOrg();
  const { pathname } = useLocation();
  const [to, setTo] = useState('');
  const [reaction, setReaction] = useState<number | null>(null);
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const send = useMutation({
    mutationFn: () =>
      api.sendFeedback({
        orgId: to || null,
        reaction,
        category,
        message: message.trim() || null,
        anonymous: Boolean(to) && anonymous,
        pageUrl: pathname,
      }),
  });
  const org = orgs.find((o) => o.id === to);
  const ready = Boolean(reaction || message.trim());

  if (send.isSuccess)
    return (
      <Modal title="Thank you" onClose={onClose}>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 size-6 shrink-0 text-india-green" aria-hidden />
          <p className="text-sm">
            Your feedback is on its way to {org ? org.name : 'the Nirvik Ready team'}. It helps us
            make learning better for everyone.
          </p>
        </div>
        <Button className="mt-5 w-full sm:w-auto" onClick={onClose}>
          Done
        </Button>
      </Modal>
    );

  return (
    <Modal title="Feedback" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) send.mutate();
        }}
      >
        {orgs.length > 0 && (
          <Field label="Send to" htmlFor="fb-to">
            <Select id="fb-to" value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">The Nirvik Ready team</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <fieldset>
          <legend className="mb-2 text-sm font-medium">How is it going?</legend>
          <div className="flex justify-between gap-1 sm:justify-start sm:gap-2">
            {Object.entries(REACTION_FACES).map(([value, r]) => {
              const n = Number(value);
              const on = reaction === n;
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={on}
                  aria-label={r.label}
                  title={r.label}
                  onClick={() => setReaction(on ? null : n)}
                  className={`flex size-12 items-center justify-center rounded-xl border text-2xl transition ${on ? 'border-primary bg-primary/15 scale-110' : 'border-border hover:bg-muted'}`}
                >
                  {r.face}
                </button>
              );
            })}
          </div>
        </fieldset>
        <fieldset>
          <legend className="mb-2 text-sm font-medium">About (optional)</legend>
          <div className="flex flex-wrap gap-2">
            {FEEDBACK_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                aria-pressed={category === c}
                onClick={() => setCategory(category === c ? null : c)}
                className={`min-h-9 rounded-full border px-3 text-sm ${category === c ? 'border-primary bg-primary/15 text-primary-strong' : 'border-border hover:bg-muted'}`}
              >
                {FEEDBACK_CATEGORY_LABELS[c]}
              </button>
            ))}
          </div>
        </fieldset>
        <Field
          label="Tell us more (optional)"
          htmlFor="fb-message"
          hint={`${message.length.toLocaleString('en-US')} / ${FEEDBACK_MAX_LENGTH.toLocaleString('en-US')}`}
        >
          <Textarea
            id="fb-message"
            rows={4}
            maxLength={FEEDBACK_MAX_LENGTH}
            value={message}
            placeholder="What works well, what's confusing, what would you like to see?"
            onChange={(e) => setMessage(e.target.value)}
          />
        </Field>
        {org && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-5 shrink-0 accent-primary"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
            />
            <span>
              Don&rsquo;t share my name with {org.name}
              <span className="block text-xs text-muted-foreground">
                Nirvik Ready&rsquo;s team can still see who sent it, only to stop misuse.
              </span>
            </span>
          </label>
        )}
        <p className="text-xs text-muted-foreground">
          Sent along with the page you&rsquo;re on: <span className="font-mono">{pathname}</span>
        </p>
        <ErrorText error={send.error} />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!ready || send.isPending}>
            {send.isPending ? 'Sending…' : 'Send feedback'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
